import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { RawDatum } from 'interfaces/chart';
import { useData } from './useData';
import { usePageCarto } from './usePageCarto';
import { GEO_CODE } from 'utils/constants';

type ChartIndicatorsContextValue = {
  selectedIndicatorIds: number[];
  setSelectedIndicatorIds: (indicatorIds: number[]) => void;
  resetIndicators: () => void;
  selectedData: RawDatum[];
};

const initialContextValue: ChartIndicatorsContextValue = {
  selectedIndicatorIds: [],
  setSelectedIndicatorIds: () => {
    /** noop **/
  },
  resetIndicators: () => {
    /** noop **/
  },
  selectedData: [],
};

const ChartIndicatorsContext =
  createContext<ChartIndicatorsContextValue>(initialContextValue);

ChartIndicatorsContext.displayName = 'ChartIndicatorsContext';

type ChartIndicatorsProviderProps = {
  children: ReactNode;
};

export const ChartIndicatorsProvider: FC<ChartIndicatorsProviderProps> = ({
  children,
}) => {
  const [selectedIndicatorIds, setSelectedIndicatorIds] = useState<number[]>(
    []
  );
  const { indicatorData, selectedGeoCode } = useData();
  const { indicators } = usePageCarto();

  const resetIndicators = useCallback(() => {
    setSelectedIndicatorIds([]);
  }, []);

  const activeIndicators = useMemo(() => {
    if (!indicators || indicators.length === 0) {
      return [];
    }
    if (!selectedIndicatorIds || selectedIndicatorIds.length === 0) {
      return indicators;
    }
    const selectedSet = new Set(selectedIndicatorIds);
    const filtered = indicators.filter((indicator) =>
      selectedSet.has(indicator.id)
    );
    return filtered.length > 0 ? filtered : indicators;
  }, [indicators, selectedIndicatorIds]);

  const selectedData = useMemo(() => {
    if (!selectedGeoCode) {
      return [];
    }

    const datum = indicatorData.find((d) => d[GEO_CODE] === selectedGeoCode);

    if (!datum) {
      return [];
    }

    if (activeIndicators.length === 0) {
      return Object.entries(datum)
        .filter(([key]) => key !== GEO_CODE)
        .map(([key, value]) => ({ key, value })) as RawDatum[];
    }

    return activeIndicators
      .map(({ name }) => ({
        key: name,
        value: datum[name],
      }))
      .filter(({ value }) => value !== undefined) as RawDatum[];
  }, [activeIndicators, indicatorData, selectedGeoCode]);

  const value = useMemo(
    () => ({
      selectedIndicatorIds,
      setSelectedIndicatorIds,
      resetIndicators,
      selectedData,
    }),
    [resetIndicators, selectedData, selectedIndicatorIds]
  );

  return (
    <ChartIndicatorsContext.Provider value={value}>
      {children}
    </ChartIndicatorsContext.Provider>
  );
};

export const useChartIndicators = () => {
  const context = useContext(ChartIndicatorsContext);
  return context;
};
